// Sidebar navigation items. Kept as data so a test can check each item's role gate (SEN-261).
import {
  Building2, ClipboardList, Cpu, Database, Files, Gauge, Inbox, LayoutDashboard, Lightbulb,
  Pin, Settings, Sparkles, Target, Wrench, Zap,
} from 'lucide-react'
import type { PageSummary } from '@poiesis/agentArtifacts/types'
import type { RoleCode } from '@/lib/roles'
import { useInboxUnread } from '@/pages/work/useInboxUnread'

export interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  /** Lowest role that sees this nav item. Omit = every role. Admins see every item. */
  minRole?: RoleCode
  /** A count shown beside the title (the Inbox's unread), read by the item itself. */
  useCount?: () => number | null
}

// Owner, 2026-10-21: three groups split by a line; the second is labelled Ontology, the third Manage —
//   Sentinel, Dashboard, Work | Sites, Meters, Assets, Targets, Initiatives | Files, Skills, Data, Connections, Settings
// The chat is the top element, named Sentinel with the outline Sentinel mark; the header carries the coloured mark
// beside the organisation's name. Decisions retired into
// the Team Inbox (owner, 2026-10-23).
export const primaryNavItems: NavItem[] = [
  { title: 'Sentinel', href: '/coo/chat', icon: Zap },
  { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  // Owner, 2026-10-23: the Inbox is a sidebar element in its own right (as Linear's), not a Work tab; below Dashboard.
  { title: 'Inbox', href: '/inbox', icon: Inbox, minRole: 'store_manager', useCount: useInboxUnread },
  { title: 'Work', href: '/work', icon: ClipboardList, minRole: 'store_manager' },
]

export const dataNavItems: NavItem[] = [
  { title: 'Sites', href: '/sites', icon: Building2 },
  { title: 'Meters', href: '/meters', icon: Gauge, minRole: 'installer' },
  { title: 'Assets', href: '/assets', icon: Wrench, minRole: 'installer' },
  { title: 'Targets', href: '/budget', icon: Target, minRole: 'facility_manager' },
  { title: 'Initiatives', href: '/initiatives', icon: Lightbulb },
]

// Automations (/coo/automations) is hidden for every role (owner, 2026-10-20: no automations in use).
export const adminNavItems: NavItem[] = [
  // Owner, 2026-10-29: the section reads Files, Skills, Data, Connections, Settings. Files replaces Uploads and
  // Skills replaces Knowledge (SEN-449/450): what the people and the agent of the workspace share; everyone
  // reads them (writing is the type's own grants).
  { title: 'Files', href: '/files', icon: Files },
  { title: 'Skills', href: '/skills', icon: Sparkles },
  // Owner, 2026-10-29: Core Data is renamed Data.
  { title: 'Data', href: '/data', icon: Database, minRole: 'facility_manager' },
  { title: 'Connections', href: '/integrations', icon: Cpu, minRole: 'installer' },
  { title: 'Settings', href: '/settings', icon: Settings },
]

export interface NavGroup {
  /** Shown above the group when the sidebar is expanded; the first group has none. */
  label?: string
  items: NavItem[]
}

/** Pinned artifact pages (owner, 2026-11-03): mine first, then those shared with me, under the first group. Only
 *  when there is at least one (no teaser UI). Their titles are the artifacts' own. */
export function pagesNavGroup(pages: { mine: PageSummary[]; shared: PageSummary[] } | null): NavGroup | null {
  const all = pages ? [...pages.mine, ...pages.shared] : []
  if (!all.length) return null
  return { label: 'Pages', items: all.map((p) => ({ title: p.title, href: `/pages/${encodeURIComponent(p.page_id)}`, icon: Pin })) }
}

/** The sidebar, top to bottom: each group is separated by a line; the last sits at the bottom. */
export const navGroups: NavGroup[] = [
  { items: primaryNavItems },
  { label: 'Ontology', items: dataNavItems },
  { label: 'Manage', items: adminNavItems },
]
